import React from "react";

/**
 * Final CTA banner — full-bleed renovation image, dark overlay, big copy.
 * Two CTAs: primary orange "Get Your Free Quote" + secondary outlined "Become a Pro Contractor".
 */

const FinalCTASection = () => {
  return (
    <section
      data-testid="final-cta-section"
      className="relative w-full overflow-hidden"
    >
      {/* BG image */}
      <div className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1600210491892-03d54c0aaf87?w=2000&q=80&auto=format&fit=crop"
          alt=""
          aria-hidden="true"
          className="w-full h-full object-cover"
        />
      </div>

      {/* Dark navy overlay + warm orange wash on top */}
      <div className="absolute inset-0 bg-[#0f172a]/80" />
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 50% 60% at 80% 20%, rgba(242,107,31,0.35) 0%, transparent 60%)",
        }}
      />
      {/* Subtle noise/grain via repeated lines */}
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            "repeating-linear-gradient(0deg, rgba(255,255,255,0.4) 0px, rgba(255,255,255,0.4) 1px, transparent 1px, transparent 4px)",
        }}
      />

      <div className="relative max-w-[1280px] mx-auto px-5 sm:px-8 py-16 sm:py-24 lg:py-36">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end">
          <div className="lg:col-span-8">
            <div className="inline-flex items-center gap-2 mb-5 sm:mb-6">
              <span className="block h-px w-8 bg-[#F26B1F]" />
              <span className="text-[10px] sm:text-[12px] font-bold tracking-[0.24em] uppercase text-[#F26B1F]">
                Your home. Done right.
              </span>
            </div>

            <h2
              className="font-extrabold text-white tracking-tight text-[32px] sm:text-[56px] lg:text-[76px] leading-[0.98]"
              style={{ fontFamily: "'Inter', system-ui, sans-serif" }}
            >
              Stop quoting.
              <br />
              Start <span style={{ color: "#F26B1F" }}>building</span>.
            </h2>

            <p className="mt-5 sm:mt-6 text-slate-200 text-[14.5px] sm:text-[18px] leading-[1.6] max-w-[560px]">
              Free quote in under 24 hours. Vetted pros. Payment held until
              the work is done right. That's it. That's the pitch.
            </p>

            <div className="mt-7 sm:mt-9 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
              <a
                href="#quote"
                data-testid="final-cta-primary"
                className="group inline-flex items-center justify-center gap-2 px-7 sm:px-8 py-4 rounded-full font-bold text-white text-[14.5px] sm:text-[15px] tracking-tight transition-all duration-300 hover:-translate-y-0.5"
                style={{
                  backgroundColor: "#F26B1F",
                  boxShadow:
                    "0 16px 36px -12px rgba(242,107,31,0.6), 0 0 0 2px rgba(255,255,255,0.95)",
                }}
              >
                Get your free quote
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  className="transition-transform duration-300 group-hover:translate-x-1"
                  aria-hidden="true"
                >
                  <path
                    d="M3 8 H13 M9 4 L13 8 L9 12"
                    stroke="currentColor"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </a>
              <a
                href="#contractor-signup"
                data-testid="final-cta-secondary"
                className="group inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full font-bold text-white text-[15px] tracking-tight border-2 border-white/40 hover:border-white hover:bg-white/10 backdrop-blur-sm transition-all duration-300"
              >
                Become a pro contractor
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                  className="text-white transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                  aria-hidden="true"
                >
                  <path
                    d="M3 11 L11 3 M5 3 H11 V9"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </a>
            </div>
          </div>

          {/* Right — 3 trust pillars */}
          <div className="lg:col-span-4">
            <ul className="space-y-5 lg:space-y-6 lg:border-l lg:border-white/15 lg:pl-8">
              {[
                {
                  k: "240+",
                  v: "Projects completed across the GTA",
                },
                {
                  k: "4.9★",
                  v: "Average homeowner review on HomeStars",
                },
                {
                  k: "12 mo",
                  v: "Workmanship warranty after final sign-off",
                },
              ].map((item, i) => (
                <li
                  key={i}
                  className="border-b border-white/10 pb-5 last:border-b-0 last:pb-0"
                >
                  <div className="text-white font-extrabold text-[28px] sm:text-[32px] leading-none tracking-tight">
                    {item.k}
                  </div>
                  <div className="mt-2 text-slate-300 text-[13.5px] leading-[1.55]">
                    {item.v}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FinalCTASection;
